import { type FormEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { IntegrationProvider } from '@lizrd/contracts';
import { connectGitHub, connectGitLab, connectIntegration } from '../lib/api';
import { PROVIDER_ACCENT } from '../lib/graph-visuals';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import { ProviderLogo } from './ProviderLogo';

const LABEL: Record<string, string> = { GITHUB: 'GitHub', GITLAB: 'GitLab', SANDBOX: 'Sandbox' };

/**
 * Connect a new source (GitHub or Sandbox) from the data-flow graph. GitHub may
 * return an install-required step (the App isn't installed on the repo yet),
 * which we surface inline with a link into the GitHub install flow. AWS has its
 * own multi-step setup modal.
 */
export function AddIntegrationModal({
  envId,
  provider,
  onClose,
  onConnected,
  mode = 'connect',
  initialBaseUrl,
  initialGroupPath,
}: {
  envId: string;
  provider: IntegrationProvider;
  onClose: () => void;
  onConnected: () => void;
  /** 'update' re-keys the token on an existing integration (same URL/group → the
   * backend reuses the row in place), vs connecting a new one. */
  mode?: 'connect' | 'update';
  initialBaseUrl?: string;
  initialGroupPath?: string;
}) {
  const isUpdate = mode === 'update';
  const [repository, setRepository] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState(initialBaseUrl ?? 'https://gitlab.com');
  const [token, setToken] = useState('');
  const [groupPath, setGroupPath] = useState(initialGroupPath ?? '');
  const [error, setError] = useState<string | null>(null);
  const [install, setInstall] = useState<{ url?: string; reason?: string } | null>(null);
  const accent = PROVIDER_ACCENT[provider] ?? '#8b93a7';
  const isGitHub = provider === 'GITHUB';
  const isGitLab = provider === 'GITLAB';

  const github = useMutation({
    mutationFn: () =>
      connectGitHub(envId, {
        repository: repository.trim(),
        displayName: displayName.trim() || undefined,
      }),
    onSuccess: (res) => {
      setError(null);
      if (res.status === 'connected') onConnected();
      else if (res.status === 'install_required') setInstall({ url: res.installUrl });
      else setInstall({ reason: res.reason });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to connect'),
  });
  const gitlab = useMutation({
    mutationFn: () =>
      connectGitLab(envId, {
        token: token.trim(),
        baseUrl: baseUrl.trim(),
        groupPath: groupPath.trim() || undefined,
        displayName: displayName.trim() || undefined,
      }),
    onSuccess: (res) => {
      if (res.status === 'connected') onConnected();
      else setError(res.reason);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to connect'),
  });
  const sandbox = useMutation({
    mutationFn: () =>
      connectIntegration(envId, {
        provider,
        displayName: displayName.trim(),
        config: {},
        credentials: {},
      }),
    onSuccess: onConnected,
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to connect'),
  });

  const pending = github.isPending || gitlab.isPending || sandbox.isPending;
  const canSubmit = isGitHub
    ? !!repository.trim()
    : isGitLab
      ? !!token.trim() && !!baseUrl.trim()
      : !!displayName.trim();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    if (isGitHub) github.mutate();
    else if (isGitLab) gitlab.mutate();
    else sandbox.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <ProviderLogo provider={provider} size={20} />
          <h3 className="font-display text-base font-bold text-bone">
            {isUpdate
              ? `Update ${LABEL[provider] ?? provider} token`
              : `Connect ${LABEL[provider] ?? provider}`}
          </h3>
        </div>
      }
    >
      {install ? (
        <div className="space-y-3 text-sm">
          {install.url ? (
            <>
              <p className="text-haze">
                The Lizrd GitHub App isn’t installed on that repository yet. Install it, then this
                domain will start receiving its data. It reads your code to build the knowledge
                graph; for Reviews it also posts comments and checks on pull requests — it never
                pushes or changes your code.
              </p>
              <a
                href={install.url}
                target="_blank"
                rel="noreferrer"
                className="inline-block rounded-sm px-3 py-2 text-sm font-semibold text-on-iris"
                style={{ backgroundColor: accent }}
              >
                Install the GitHub App ↗
              </a>
              <p className="text-xs text-muted">After installing, reopen this dialog to connect.</p>
            </>
          ) : (
            <Alert variant="danger">{install.reason ?? 'GitHub is unavailable right now.'}</Alert>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          {isGitHub && (
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                Repository
              </span>
              <Input
                placeholder="owner/repo"
                value={repository}
                onChange={(e) => setRepository(e.target.value)}
              />
              <div className="mt-2 rounded-md border border-hairline bg-surface-2/30 p-3 text-[0.72rem] leading-relaxed text-muted">
                <div className="mb-1 font-semibold text-bone">
                  What the Lizrd GitHub App accesses
                </div>
                <ul className="ml-3.5 list-disc space-y-0.5">
                  <li>Reads repository contents + metadata to build the knowledge graph.</li>
                  <li>
                    For Reviews: posts pull-request comments, checks, and commit statuses — so it
                    can comment on a PR and report a pass/fail status before merge.
                  </li>
                  <li>
                    Never pushes, edits, or deletes your code. You approve every permission on
                    GitHub.
                  </li>
                </ul>
              </div>
            </label>
          )}
          {isGitLab && (
            <>
              {isUpdate && (
                <div className="rounded-md border border-iris/30 bg-iris/10 p-3 text-[0.72rem] leading-relaxed text-bone">
                  Updating the token on this GitLab connection — the URL and group stay the same, so
                  your history and connection are kept. Paste a new token (use the <code>api</code>{' '}
                  scope to enable Reviews).
                </div>
              )}
              <label className="block text-sm">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                  GitLab URL
                </span>
                <Input
                  placeholder="https://gitlab.com"
                  value={baseUrl}
                  disabled={isUpdate}
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
                <span className="mt-1 block text-[0.7rem] text-muted">
                  Your self-managed host, or leave as gitlab.com.
                </span>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                  Access token
                </span>
                <Input
                  type="password"
                  placeholder="glpat-…"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
                <span className="mt-1 block text-[0.7rem] leading-relaxed text-muted">
                  Create a <span className="text-haze">Group</span> or{' '}
                  <span className="text-haze">Project Access Token</span> (GitLab → Settings →
                  Access Tokens). Use <code>read_api</code> + <code>read_repository</code> for code
                  intelligence, or the <code>api</code> scope (Maintainer/Owner role) to also post
                  Reviews on merge requests. Stored AES-256 encrypted.
                </span>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                  Group path (optional)
                </span>
                <Input
                  placeholder="acme/platform"
                  value={groupPath}
                  disabled={isUpdate}
                  onChange={(e) => setGroupPath(e.target.value)}
                />
                <span className="mt-1 block text-[0.7rem] text-muted">
                  Limit to one group (incl. subgroups). Leave blank for all projects the token can
                  see.
                </span>
              </label>
              <div className="rounded-md border border-hairline bg-surface-2/30 p-3 text-[0.72rem] leading-relaxed text-muted">
                <div className="mb-1 font-semibold text-bone">What Lizrd does with this token</div>
                <ul className="ml-3.5 list-disc space-y-0.5">
                  <li>Reads your repository files to build the knowledge graph.</li>
                  <li>
                    With the <code>api</code> scope and Reviews enabled: posts a review comment + a
                    commit status on merge requests, and sets up the merge-request webhook for you.
                  </li>
                  <li>
                    Never pushes, edits, or deletes your code. The token is encrypted at rest and
                    you can revoke it any time in GitLab.
                  </li>
                </ul>
              </div>
            </>
          )}
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
              Name{isGitHub || isGitLab ? ' (optional)' : ''}
            </span>
            <Input
              placeholder={
                isGitHub
                  ? 'Defaults to the repository'
                  : isGitLab
                    ? 'Defaults to the host/group'
                    : 'e.g. Production sandbox'
              }
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          {error && <Alert variant="danger">{error}</Alert>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={pending || !canSubmit}>
              {pending
                ? isUpdate
                  ? 'Updating…'
                  : 'Connecting…'
                : isUpdate
                  ? 'Update token'
                  : 'Connect'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
